import { FormEvent, useState } from 'react';
import { Heart, LoaderCircle, MoreHorizontal, Send, Trash2 } from 'lucide-react';
import { AppUser } from '../../types';
import { usePostComments } from '../../hooks/usePostComments';
import { formatRelativeTime } from '../../utils/relativeTime';

interface PostCommentsSectionProps {
  postId: string;
  currentUser?: AppUser | null;
  onOpenAuth: () => void;
  onCommentCountChange: (delta: number) => void;
  blockedUserIds?: ReadonlySet<string>;
  onOpenProfile?: (uid: string) => void;
}

export function PostCommentsSection({
  postId,
  currentUser,
  onOpenAuth,
  onCommentCountChange,
  blockedUserIds = new Set(),
  onOpenProfile
}: PostCommentsSectionProps) {
  const { comments, addComment, deleteComment, loadMore, toggleCommentLike, isLoading, hasMore, error } =
    usePostComments(postId, currentUser || null);
  const [text, setText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [workingCommentIds, setWorkingCommentIds] = useState<Set<string>>(new Set());
  const [openActionsCommentId, setOpenActionsCommentId] = useState<string | null>(null);
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!currentUser) {
      onOpenAuth();
      return;
    }
    const cleanText = text.trim();
    if (!cleanText || isSubmitting) return;

    setIsSubmitting(true);
    setMessage('');
    setText('');
    onCommentCountChange(1);
    try {
      await addComment(cleanText);
    } catch (submitError) {
      setText(cleanText);
      onCommentCountChange(-1);
      setMessage(submitError instanceof Error ? submitError.message : 'Could not post your comment.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLike = async (commentId: string) => {
    if (!currentUser) {
      onOpenAuth();
      return;
    }
    if (workingCommentIds.has(commentId)) return;
    setWorkingCommentIds(previous => new Set(previous).add(commentId));
    setMessage('');
    try {
      await toggleCommentLike(commentId);
    } catch (likeError) {
      setMessage(likeError instanceof Error ? likeError.message : 'Could not update comment like.');
    } finally {
      setWorkingCommentIds(previous => {
        const next = new Set(previous);
        next.delete(commentId);
        return next;
      });
    }
  };

  const handleDelete = async (commentId: string) => {
    setWorkingCommentIds(previous => new Set(previous).add(commentId));
    setMessage('');
    try {
      await deleteComment(commentId);
      onCommentCountChange(-1);
    } catch (deleteError) {
      setMessage(deleteError instanceof Error ? deleteError.message : 'Could not delete your comment.');
    } finally {
      setWorkingCommentIds(previous => {
        const next = new Set(previous);
        next.delete(commentId);
        return next;
      });
    }
  };

  return (
    <section aria-label="Post comments" className="border-t border-slate-100 px-4 py-4">
      <form onSubmit={event => void handleSubmit(event)} className="flex items-center gap-2">
        <img
          src={currentUser?.photoURL || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100'}
          alt={currentUser?.displayName || 'Your avatar'}
          className="h-9 w-9 shrink-0 rounded-full object-cover"
        />
        <input
          value={text}
          onChange={event => setText(event.target.value)}
          onFocus={() => {
            if (!currentUser) onOpenAuth();
          }}
          placeholder={currentUser ? 'Write a comment...' : 'Sign in to comment'}
          maxLength={1000}
          aria-label="Write a comment"
          className="min-w-0 flex-1 rounded-full border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-blue-400 focus:bg-white"
        />
        <button
          type="submit"
          disabled={!text.trim() || isSubmitting}
          className="inline-flex h-10 min-w-16 items-center justify-center gap-1 rounded-full bg-blue-900 px-3 text-xs font-bold text-white hover:bg-blue-950 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Post
        </button>
      </form>

      {message && <p role="alert" className="mt-2 text-xs text-rose-600">{message}</p>}

      <div className="mt-4 space-y-4">
        {comments.filter(comment => !blockedUserIds.has(comment.uid)).map(comment => (
          <article key={comment.id} className="flex items-start gap-2.5">
            <button
              type="button"
              onClick={() => comment.userId && onOpenProfile?.(comment.userId)}
              className="shrink-0"
              aria-label={`Open ${comment.userName}'s profile`}
            >
              <img
                src={comment.userAvatar || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100'}
                alt=""
                className="h-8 w-8 rounded-full object-cover"
              />
            </button>
            <div className="min-w-0 flex-1">
              <p className="break-words text-sm text-slate-800">
                <button
                  type="button"
                  onClick={() => comment.userId && onOpenProfile?.(comment.userId)}
                  className="mr-1 font-bold text-slate-900 hover:underline"
                >
                  {comment.userName}
                </button>
                {comment.text}
              </p>
              <div className="mt-1 flex items-center gap-3 text-[11px] text-slate-400">
                <span>{formatRelativeTime(comment.createdAt)}</span>
                <button
                  type="button"
                  aria-pressed={comment.isLiked}
                  disabled={workingCommentIds.has(comment.id)}
                  onClick={() => void handleLike(comment.id)}
                  className={`inline-flex min-h-8 items-center gap-1 font-semibold ${
                    comment.isLiked ? 'text-rose-600' : 'text-slate-500 hover:text-rose-600'
                  }`}
                >
                  <Heart className={`h-3.5 w-3.5 ${comment.isLiked ? 'fill-current' : ''}`} />
                  Like {comment.likesCount > 0 ? comment.likesCount : ''}
                </button>
                {currentUser?.uid === comment.uid && !comment.id.startsWith('pending-') && (
                  <div className="relative">
                    <button
                      type="button"
                      aria-label="Comment actions"
                      aria-expanded={openActionsCommentId === comment.id}
                      onClick={() => setOpenActionsCommentId(value => value === comment.id ? null : comment.id)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-slate-100"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                    {openActionsCommentId === comment.id && (
                      <button
                        type="button"
                        disabled={workingCommentIds.has(comment.id)}
                        onClick={() => {
                          setOpenActionsCommentId(null);
                          void handleDelete(comment.id);
                        }}
                        className="absolute right-0 top-full z-10 inline-flex min-w-24 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-rose-700 shadow-lg disabled:opacity-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </article>
        ))}

        {comments.length === 0 && !isLoading && !error && (
          <p className="py-3 text-center text-sm text-slate-500">
            No comments yet. Be the first to comment.
          </p>
        )}
        {isLoading && (
          <div className="flex justify-center py-3" role="status" aria-label="Loading comments">
            <LoaderCircle className="h-5 w-5 animate-spin text-blue-800" />
          </div>
        )}
        {error && (
          <div role="alert" className="flex items-center justify-between gap-3 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">
            <span>Could not load comments.</span>
            <button type="button" onClick={loadMore} className="font-bold underline">Retry</button>
          </div>
        )}
        {hasMore && !isLoading && !error && comments.length > 0 && (
          <button type="button" onClick={loadMore} className="min-h-9 text-xs font-bold text-blue-800 hover:underline">
            Load more comments
          </button>
        )}
      </div>
    </section>
  );
}
